import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  output,
  inject,
  input,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import {
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import {
  ReportFilterService,
  TimeconversionService,
  ToastMessageService,
  PaymentGatewayService
} from '@fitness-force/api';
import { EzypayDocumentType } from '@fitness-force/enum';
import { DataGridComponent, NoSettlementsComponent } from '@fitness-force/grid';
import { SettlementActionsComponent } from '@fitness-force/grid-utility/action';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import type { TENANTSINTERFACE } from '@fitness-force/models';
import { FileSaverService } from 'ngx-filesaver';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { PopoverModule } from 'primeng/popover';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { SettlementStatusComponent } from '../../../data-grid/utility/settlement-status/settlement-status.component';
import { CurrencyByCodeComponent } from '@fitness-force/grid-utility/currency';
const EXCEL_EXTENSION = 'xlsx';
const PDF_EXTENSION = 'pdf';

@Component({
  selector: 'ezypay-settlement',
  standalone: true,
  imports: [
    DataGridComponent,
    TranslateModule,
    ButtonModule,
    PopoverModule,
    DatePickerModule,
    FormsModule,
    ReactiveFormsModule,
    DateTimeFormatPipe,
  ],
  providers: [],
  templateUrl: './ezypay-settlement.component.html',
  styleUrl: './ezypay-settlement.component.css',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class EzypaySettlementComponent implements OnInit, OnChanges {
  private readonly store = inject(Store);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly paymentGatewayService = inject(PaymentGatewayService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly translate = inject(TranslateService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly timeService = inject(TimeconversionService);
  private readonly reportFilterService = inject(ReportFilterService);
  private readonly _FileSaverService = inject(FileSaverService);

  readonly tenantId = input<number>(0);
  readonly downloadBtnClicked = input<boolean>(false);
  readonly UpdateBtnClickedEvent = output<boolean>({
    alias: 'UpdateBtnClickedEvent',
  });

  localTenantId: number = 0;
  columns$: any = new BehaviorSubject([]);
  tableData$: any = new BehaviorSubject([]);
  totalItems$ = new BehaviorSubject(0);
  getAccessTenantList$: Observable<TENANTSINTERFACE> = new Subject();
  selectAllData = new BehaviorSubject(false);
  totalRecordLength = new BehaviorSubject({
    totalPage: 10,
    totalItems: 10,
    currentPage: 0,
  });
  pageNumber = 1;
  pageSize = 10;
  emptyStateComponent$ = new BehaviorSubject<any>(NoSettlementsComponent);
  Params: any = new Map();
  loading = false;
  customFilterForm: UntypedFormGroup = new UntypedFormGroup({});

  constructor() {
    this.customFilterForm = this.fb.group({
      from: [new Date(new Date().getFullYear(), new Date().getMonth(), 1)],
      until: [new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)],
    });
    this.customFilterForm.updateValueAndValidity();
  }

  ngOnInit(): void {
    this.Params.set('PageNumber', {
      name: 'PageNumber',
      value: this.pageNumber,
    });
    this.Params.set('PageSize', { name: 'PageSize', value: this.pageSize });
    this.Params.set('from', {
      name: 'from',
      value: this.timeService.ConvertLocalDateTimeToUTC(
        this.customFilterForm.controls.from.value,
      ),
    });
    this.Params.set('until', {
      name: 'until',
      value: this.timeService.ConvertLocalDateTimeToUTC(
        this.customFilterForm.controls.until.value,
      ),
    });
    this.totalRecordLength.next({
      totalPage: 10,
      totalItems: 10,
      currentPage: 0,
    });
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.localTenantId = tenant.selected_tenant.id;
      if (this.localTenantId > 0) {
        this.getPaymentGatewaySettlements();
      }
    });
  } 

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (
      changes.downloadBtnClicked?.currentValue != undefined && changes.downloadBtnClicked.previousValue != undefined && 
      changes.downloadBtnClicked.currentValue != changes.downloadBtnClicked.previousValue && changes.downloadBtnClicked.currentValue == true
    ) {
      this.downloadSettlements();
    }
  }
  
  getPaymentGatewaySettlements() {
    this.loading = true;
    this.paymentGatewayService
      .getSettlementByTenantId(this.localTenantId, [...this.Params.values()])
      .subscribe({
        next: async (res: any) => {
          if (res.items) {
            res.items.forEach((element: any) => {
              element.settlement_payout_method_id = 'Bank';
            });
          }
          this.tableData$.next(res.items ? res.items : []);
          this.totalRecordLength.next({
            totalPage: res.total_pages,
            totalItems: res.total_items,
            currentPage: res.current_page,
          });
          this.totalItems$.next(res.total_items);
          this.totalRecordLength.next({
            totalPage: res.total_pages,
            totalItems: res.total_items,
            currentPage: res.current_page,
          });
          this.GetTableColumns();
          this.selectAllData.next(true);
          this.loading = false;
          this.cd.detectChanges();
        },
        error: (error) => {
          this.loading = false;
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  GetTableColumns() {
    this.translate
      .get([
        'SETTLEMENTS.SETTLEMENT_NO',
        'SETTLEMENTS.DATE',
        'SETTLEMENTS.REVENUE',
        'SETTLEMENTS.DEDUCTION',
        'SETTLEMENTS.SETTLEMENT',
        'SETTLEMENTS.PAYMENT_METHOD',
        'SETTLEMENTS.ACCOUNT_NO',
        'SETTLEMENTS.STATUS',
        'SETTLEMENTS.FAILED_REASON',
        'SETTLEMENTS.CREATED_ON',
      ])
      .subscribe((translations) => {
        this.TableColumnsTransalation(translations);
      });
  }

  TableColumnsTransalation(translations: any) {
    this.columns$.next([
      {
        title: translations['SETTLEMENTS.DATE'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: {
            KeyName: 'date',
            format: 'defaultformat',
          },
        },
      },
      {
        title: translations['SETTLEMENTS.SETTLEMENT_NO'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'number',
        type: 'default',
      },
      // {
      //   title: translations['SETTLEMENTS.REVENUE'],
      //   classes: 'py-3 white-space-nowrap',
      //   sortable: true,
      //   filterable: true,
      //   dataProperty: 'revenue_value',
      //   type: 'component',
      //   data: {
      //     name: CurrencyComponent,
      //     fetch_key: 'revenue_value',
      //   },
      // },
      // {
      //   title: translations['SETTLEMENTS.DEDUCTION'],
      //   classes: 'py-3 white-space-nowrap',
      //   sortable: true,
      //   filterable: true,
      //   dataProperty: 'deduction_value',
      //   type: 'component',
      //   data: {
      //     name: CurrencyComponent,
      //     fetch_key: 'deduction_value',
      //   },
      // },
      {
        title: translations['SETTLEMENTS.SETTLEMENT'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'amount_value',
        type: 'component',
        data: {
          name: CurrencyByCodeComponent,
          fetch_key: 'amount_value',
          additionData: true,
          aData: { currencyCodeKey: 'amount_currency' },
        },
      },
      {
        title: translations['SETTLEMENTS.PAYMENT_METHOD'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'settlement_payout_method_id',
        type: 'default',
      },
      {
        title: translations['SETTLEMENTS.ACCOUNT_NO'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'last_4_digits',
        type: 'default',
      },
      {
        title: translations['SETTLEMENTS.STATUS'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'status',
        type: 'component',
        data: {
          name: SettlementStatusComponent,
          additionData: true,
          aData: { fetch_key: 'status' },
        },
      },
      {
        title: translations['SETTLEMENTS.FAILED_REASON'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'failed_settlement_reason',
        type: 'default',
      },
      {
        title: translations['SETTLEMENTS.CREATED_ON'],
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: 'created_on',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: {
            KeyName: 'created_on',
            format: 'defaultformat',
          },
        },
      },
      {
        title: '',
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        dataProperty: null,
        type: 'component',
        data: { name: SettlementActionsComponent },
      },
    ]);
  }

  applyFilter(form: FormGroup) {
    if (form.controls.from.value && form.controls.until.value) {
      this.Params.set('from', {
        name: 'from',
        value: this.timeService.ConvertLocalDateTimeToUTC(
          form.controls.from.value,
        ),
      });
      this.Params.set('until', {
        name: 'until',
        value: this.timeService.ConvertLocalDateTimeToUTC(
          form.controls.until.value,
        ),
      });
    }
    this.getPaymentGatewaySettlements();
  }

  fetchNextSetData(e: any) {
    this.pageNumber = e.pn;
    this.pageSize = e.ps;
    this.Params.set('PageNumber', {
      name: 'PageNumber',
      value: this.pageNumber,
    });
    this.Params.set('PageSize', { name: 'PageSize', value: this.pageSize });
    this.getPaymentGatewaySettlements();
  }

  downloadSettlements() {
    const payload = {
      from: this.timeService.ConvertLocalDateTimeToUTC(this.getFromDate()),
      until: this.timeService.ConvertLocalDateTimeToUTC(this.getUntilDate()),
    };

    this.paymentGatewayService
      .exportSettlementByTenantId(this.localTenantId, payload)
      .subscribe({
        next: async (res: any) => {
          this.UpdateBtnClickedEvent.emit(true);
          this.reportFilterService.downloadFile(res.url).subscribe((res) => {
            this._FileSaverService.save(
              res,
              'settlement_report' +
                new Date().getTime() +
                '.' +
                EXCEL_EXTENSION,
            );
          });
          this.translate
            .get('SETTLEMENTS.DOWNLOAD_SUCCESS')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
        },
        error: (error) => {
          this.UpdateBtnClickedEvent.emit(false);
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  onActionClick(e: any) {
    if (e && e.action == 'pdf') {
      this.downloadSettlementByTypes(
        e.columnData.number,
        EzypayDocumentType.summary_report,
      );
    } else if (e && e.action == 'csv') {
      this.downloadSettlementByTypes(
        e.columnData.number,
        EzypayDocumentType.detail_report,
      );
    } else if (e && e.action == 'taxinvoice') {
      this.downloadSettlementByTypes(
        e.columnData.number,
        EzypayDocumentType.tax_invoice,
      );
    } else {
      this.toastMessageService.showErrorMessage('Invalid Action');
    }
  }

  downloadSettlementByTypes(settlementId: number, type: EzypayDocumentType) {
    const payload = {
      sendEmail: true,
      documentType: type,
      merchantId: '',
    };

    this.paymentGatewayService
      .downloadSettlementById(this.localTenantId, settlementId, payload)
      .subscribe({
        next:(res: any) => {
          if(res.url) {
          this.reportFilterService.downloadFile(res.url).subscribe({
            next: (res) => {
            if (type == EzypayDocumentType.summary_report)
              this._FileSaverService.save(
                res,
                'settlement_summary_report' +
                  new Date().getTime() +
                  '.' +
                  PDF_EXTENSION,
              );
            else if (type == EzypayDocumentType.detail_report)
              this._FileSaverService.save(
                res,
                'settlement_detail_report' +
                  new Date().getTime() +
                  '.' +
                  EXCEL_EXTENSION,
              );
            else if (type == EzypayDocumentType.tax_invoice)
              this._FileSaverService.save(
                res,
                'settlement_tax_invoice' +
                  new Date().getTime() +
                  '.' +
                    PDF_EXTENSION,
                );

                this.translate
                .get('SETTLEMENTS.DOWNLOAD_SUCCESS')
                .subscribe((translation) => {
                  this.toastMessageService.showSuccess(translation);
                });
            },
            error: (error) => {
              this.toastMessageService.showError(error);
                this.cd.detectChanges();
              },
            });
          }
          else {
            this.toastMessageService.showError('No document found');
            this.cd.detectChanges();
          }
        },
        error: (error) => {
          this.toastMessageService.showError(error);
          this.cd.detectChanges();
        },
      });
  }

  getFromDate() {
    return this.customFilterForm.controls.from.value;
  }

  getUntilDate() {
    return this.customFilterForm.controls.until.value;
  }
}
